/*    
<summary>
  This functional component "Add Branch" provides form to add new branch or update existing branch.
  Developer:Mohammad Saquib Khan, Created Date:12-April-2024
</summary>
<returns>Returns JSX</returns>
*/
import { Field, Form, Formik } from "formik";
import moment from "moment";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import {
  getOrgIdForNovastar,
  isFreeTextSpecialDateTimeOverlap,
  isSpecialDateTimeOverlap,
  isStartDateTimeGreaterThanEndDateTime,
} from "../../../constants/constants";
import { specialScheduleTypeOptions } from "../../../constants/options.constant";
import { useStore } from "../../../contexts/store-provider";
import config from "../../../helpers/config-helper";
import { getAccessOrgIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { IOption } from "../../../models/ICommon";
import { ISpecialSchedulerVM } from "../../../models/response/IPriceSchedulerResponse";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikTimeFieldMUI from "../../../shared-components/FormikFormHandlers/FormikTimeFieldMUI";
import {
  AddFreeTextSpecialSchedulerSchema
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../shared-components/button/loader-button";
import Label from "../../../shared-components/label/label";
import { formatMessage } from "../../../translations/format-message";
import "./add-edit-free-text-special-scheduler-form.css";

interface IProps {
  id: number;
  allFreeTextFolders: IOption[];
  allSpecialSchedules: ISpecialSchedulerVM[];
  allAvailableImages: IOption[];
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  isLoading: boolean;
  initialValues: any;
}

const appConfig = config();
const timeFormat = appConfig.REACT_APP_TIME_FORMAT;
const dateFormat = appConfig.REACT_APP_DATE_FORMAT;

const AddUpdateFreeTextSpecialSchedulerForm = (props: IProps) => {
  const [selectedWeekday, setSelectedWeekdayId] = useState<number>(0);
  const [selectedFolderId, setFolderId] = useState<number>(-1);
  const { preferencesStore, mediaStore } = useStore();
  const { getFreeTextSchedulerAccess } = preferencesStore;
  const { GetAllFreeTextByFolderId } = mediaStore;
  const validateTime = (value: any) => {
    let error;
    if (value === null) error = "field_required";
    else if (value?.$d && value?.$d.toString() === "Invalid Date")
      error = "invalid_time_format";
    return error;
  };

  useEffect(() => {
    if (selectedFolderId > 0) {
      GetAllFreeTextByFolderId(
        selectedFolderId,
        getAccessOrgIdFromLocalStorage(),
      );
    }
  }, [selectedFolderId]);

  const handleCopyClick = (url: any) => {
    const textField = document.createElement("textarea");
    textField.innerText = url;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
    toast.success(formatMessage("url_copied"));
  };

  return (
    <>
      <Modal
        className="special-modal fancy-modal"
        show={true}
        size="lg"
        centered
        onHide={() => {}}
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="add_special_scheduler" />
            ) : (
              <FormattedMessage id="update_special_scheduler" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validationSchema={AddFreeTextSpecialSchedulerSchema}
          validateOnBlur={false}
          onSubmit={async (values) => {
            debugger
            let tempStartTime = values.StartTime?.$d
              ? moment(values.StartTime?.$d).format(timeFormat)
              : moment(values.StartTime).format(timeFormat);

            let tempEndTime = values.EndTime?.$d
              ? moment(values.EndTime?.$d).format(timeFormat)
              : moment(values.EndTime).format(timeFormat);

            let tempStartDate = values.StartDate?.$d
              ? moment(values.StartDate.$d).format(dateFormat)
              : moment(values.StartDate).format(dateFormat);

            let tempEndDate = values.EndDate?.$d
              ? moment(values.EndDate.$d).format(dateFormat)
              : moment(values.EndDate).format(dateFormat);

            if (
              isStartDateTimeGreaterThanEndDateTime(
                tempStartDate,
                tempStartTime,
                tempEndDate,
                tempEndTime,
              )
            ) {
              toast.error(formatMessage("start_date_is_greater_than_end_date"));
            } else {
              if (
                isFreeTextSpecialDateTimeOverlap(
                  tempStartDate,
                  tempStartTime,
                  tempEndDate,
                  tempEndTime,
                  props.allSpecialSchedules,
                  props.id,
                  Number(values.ScheduleType),
                )
              ) {
                toast.error(formatMessage("special_scheduler_overlapped"));
              } else {
                props.submitHandler({
                  ...values,
                  startTime: tempStartTime,
                  endTime: tempEndTime,
                  startDate: tempStartDate,
                  endDate: tempEndDate,
                });
              }
            }
          }}
        >
          {({ values }) => {
            return (
              <Form className="user-form">
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} />
                ) : null}
                <Modal.Body>
                  <div className="row">
                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="start_date"
                      />
                      <div className="">
                        <Field
                          name="StartDate"
                          validate={validateTime}
                          as={FormikDateFieldMUI}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="start_time"
                      />
                      <div className="">
                        <Field
                          name="StartTime"
                          validate={validateTime}
                          as={FormikTimeFieldMUI}
                          ampm="false"
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="end_date"
                      />
                      <div className="">
                        <Field
                          name="EndDate"
                          validate={validateTime}
                          as={FormikDateFieldMUI}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="end_time"
                      />
                      <div className="">
                        <Field
                          name="EndTime"
                          validate={validateTime}
                          as={FormikTimeFieldMUI}
                          ampm="false"
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="schedule_type"
                      />
                      <div className="">
                        <Field
                          data-testid="schedule_type"
                          className="form-control"
                          name="ScheduleType"
                          options={specialScheduleTypeOptions}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-6">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="select_folder"
                      />
                      <div className="">
                        <Field
                          className="form-control"
                          name="FolderId"
                          disabled={props.id > 0 ? true : false}
                          placeholder="select_folder"
                          options={props.allFreeTextFolders}
                          as={FormikFormSelect}
                        />
                      </div>
                      {(() => {
                        setFolderId(Number(values?.FolderId));
                        return null;
                      })()}
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_1"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/1`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/1`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data1"
                          className="form-control"
                          name="Data1"
                          placeholder="data_1"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_2"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/2`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/2`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data2"
                          className="form-control"
                          name="Data2"
                          placeholder="data_2"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_3"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/3`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/3`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data3"
                          className="form-control"
                          name="Data3"
                          placeholder="data_3"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_4"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/4`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/4`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data4"
                          className="form-control"
                          name="Data4"
                          placeholder="data_4"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_5"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/5`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/5`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data5"
                          className="form-control"
                          name="Data5"
                          placeholder="data_5"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2 col-4">
                      <Label
                        className="label-style col-form-label pb-0"
                        required={false}
                        label="data_6"
                      />

                      <div className="d-flex align-items-center ">
                        <button
                          type="button"
                          className="ps-0"
                          onClick={() =>
                            handleCopyClick(
                              `${
                                process.env.REACT_APP_BASE_URL
                              }ft/${getOrgIdForNovastar()}/${
                                getFreeTextSchedulerAccess.plId
                              }/6`,
                            )
                          }
                        >
                          <svg
                            id="Layer_2"
                            height="14"
                            viewBox="0 0 24 24"
                            width="14"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#0091ff"
                          >
                            <path d="m5.4521 22h9.0957c1.7485 0 3.1822-1.3118 3.4064-3h.5936c1.9034 0 3.4522-1.5488 3.4522-3.4521v-10.0958c0-1.9033-1.5488-3.4521-3.4521-3.4521h-9.0958c-1.9033 0-3.4521 1.5488-3.4521 3.4521v.5479h-.5479c-1.9033 0-3.4521 1.5488-3.4521 3.4521v9.0957c0 1.9034 1.5488 3.4522 3.4521 3.4522zm2.5479-16.5479c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v10.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-.5479v-7.5479c0-1.9033-1.5488-3.4521-3.4521-3.4521h-6.5479zm-4 4c0-.8007.6514-1.4521 1.4521-1.4521h9.0957c.8008 0 1.4522.6514 1.4522 1.4521v9.0957c0 .8008-.6514 1.4522-1.4521 1.4522h-9.0958c-.8007 0-1.4521-.6514-1.4521-1.4521z" />
                          </svg>
                        </button>
                        <p className="fs-xs text-muted">{`${
                          process.env.REACT_APP_BASE_URL
                        }ft/${getOrgIdForNovastar()}/${
                          getFreeTextSchedulerAccess.plId
                        }/6`}</p>
                      </div>
                      <div className="">
                        <Field
                          id="Data6"
                          className="form-control"
                          name="Data6"
                          placeholder="data_6"
                          disabled={values?.FolderId == -1 ? true : false}
                          options={props.allAvailableImages}
                          as={FormikFormSelect}
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <div className="modal-footer">
                  <div className="row tab-action g-0">
                    <div className="col-12">
                      <Button
                        variant="light-danger"
                        type="button"
                        onClick={props.onModalClosed}
                        disabled={props.isLoading}
                      >
                        <FormattedMessage id="button_cancel" />
                      </Button>
                      <LoaderButton
                        type="submit"
                        isLoading={props.isLoading}
                        text="button_save"
                      />
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AddUpdateFreeTextSpecialSchedulerForm;
